import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchTeamSummary } from './api';
import { safeApiCall } from '../../../utils/apiHelpers';

const LeaveTypeCell = ({ leave, onClick }) => (
  <td className="text-center cursor-pointer hover:bg-blue-50" onClick={onClick}>
    <div className="font-semibold text-blue-700">{leave.yearly.allocated}</div>
    <div className="text-xs text-gray-500">Allocated</div>
    <div className="font-semibold text-green-600">{leave.yearly.remaining}</div>
    <div className="text-xs text-gray-500">Remaining</div>
  </td>
);

const TeamMembers = () => {
  const navigate = useNavigate();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const res = await safeApiCall(fetchTeamSummary);
      if (res) setTeam(res.data);
      setLoading(false);
    })();
  }, []);

  const openLeaveModal = (employee, leave) => {
    setSelectedEmployee(employee);
    setSelectedLeave(leave);
  };

  const closeModal = () => {
    setSelectedLeave(null);
    setSelectedEmployee(null);
  };

  const viewProfile = (employeeId) => {
    navigate(`/leavetracker/team/profile/${employeeId}`);
  };

  if (loading) return <div className="p-4 text-center">Loading...</div>;

  // Collect all leave types for header
  const leaveTypes = Array.from(
    new Set(team.flatMap(emp => emp.leave_balance.map(lb => lb.leave_type)))
  );

  return (
    <div className="p-4">
      <div className="max-h-[75vh] overflow-y-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {team.map(emp => (
            <div key={emp.id} className="bg-white shadow rounded-lg p-4 flex flex-col gap-2 border hover:shadow-md transition">
              <div className="flex flex-row items-center justify-between mb-2">
                <div className="flex items-center gap-4">
                  <div className="bg-blue-100 text-blue-700 font-bold rounded px-3 py-1 text-xs">{emp.emp_code}</div>
                  <div 
                    onClick={() => viewProfile(emp.id)}
                    className="font-semibold text-lg cursor-pointer transition-all duration-300 hover:text-blue-600 hover:scale-105"
                  >
                    {emp.first_name} {emp.last_name}
                  </div>
                </div>
              </div>
              <div className="text-gray-500 text-sm mb-2">Phone: {emp.phone_number}</div>
              <div className="flex flex-wrap gap-2">
                {leaveTypes.map(type => {
                  const leave = emp.leave_balance.find(lb => lb.leave_type === type);
                  return leave ? (
                    <div
                      key={type}
                      className="cursor-pointer border rounded-lg px-2 py-1 bg-gray-50 hover:bg-blue-50 flex flex-col items-center min-w-[100px] shadow-sm text-xs"
                      onClick={() => openLeaveModal(emp, leave)}
                    >
                      <div className="font-semibold text-blue-700 text-xs mb-1">{type}</div>
                      <div className="text-gray-600">
                        Leaves: ({leave.yearly.remaining}/{leave.yearly.allocated})
                      </div>
                    </div>
                  ) : null;
                })}
              </div>
              {emp.upcoming_leaves && emp.upcoming_leaves.length > 0 && (
                <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded p-2 w-full text-xs">
                  <div className="font-semibold text-yellow-700 mb-1">Upcoming Leaves:</div>
                  <ul className="list-disc ml-4">
                    {emp.upcoming_leaves.map((leave, idx) => (
                      <li key={idx} className="mb-0.5">
                        <span className="font-medium">{leave.date}</span> - {leave.leave_type} ({leave.leave_day_type}) <span className="italic text-gray-500">[{leave.status}]</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Modal for quarter summary */}
      {selectedLeave && selectedEmployee && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg max-w-lg w-full p-6 relative">
            <button className="absolute top-2 right-2 text-xl text-gray-500 hover:text-gray-700" onClick={closeModal}>&times;</button>
            <h3 className="text-lg font-bold mb-2">
              {selectedEmployee.first_name} {selectedEmployee.last_name} - {selectedLeave.leave_type}
            </h3>
            {/* Highlight current quarter row */}
            <table className="w-full text-sm mb-2">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-2 py-1">Quarter</th>
                  <th className="px-2 py-1">Allocated</th>
                  <th className="px-2 py-1">Taken</th>
                  <th className="px-2 py-1">Remaining</th>
                  <th className="px-2 py-1">Carry Forward</th>
                </tr>
              </thead>
              <tbody>
                {selectedLeave.quarters.map(q => {
                  // Get current quarter based on current month
                  const now = new Date();
                  const currentMonth = now.getMonth(); // 0-indexed
                  const currentQuarter = Math.floor(currentMonth / 3) + 1;
                  const isCurrent = q.quarter === currentQuarter;
                  return (
                    <tr key={q.quarter} className={isCurrent ? "font-bold bg-blue-50" : ""}>
                      <td className="px-2 py-1 text-center">Q{q.quarter}</td>
                      <td className="px-2 py-1 text-center">{q.allocated}</td>
                      <td className="px-2 py-1 text-center">{q.taken}</td>
                      <td className="px-2 py-1 text-center">{q.remaining}</td>
                      <td className="px-2 py-1 text-center">{q.carry_forward}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="text-xs text-yellow-700 bg-yellow-50 rounded px-2 py-1 mb-2">
              Note: Only the <span className="font-semibold">current quarter</span> values are accurate. All future quarter numbers will change based on your leaves used.
            </div>
            <div className="text-right">
              <button className="mt-2 px-4 py-1 bg-blue-600 text-white rounded hover:bg-blue-700" onClick={closeModal}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamMembers;
